/* Inxiz PDP "Complete your wardrobe" offer panel (21 Oct 2026): ladder 2 items 15 % / 3+ items 20 % counted on
   PDP-local units (main quantity + ticked rows), progress, free US shipping estimate, one cart/add.js for the main
   product plus ticked rows. Runs only inside .inxiz-pdp-demo. */
if (!customElements.get('inxiz-cross-sell')) {
  customElements.define('inxiz-cross-sell', class extends HTMLElement {
    connectedCallback() {
      if (!this.closest('.inxiz-pdp-demo')) return;
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      this.scope = this.closest('[data-inxiz-pdp-info]');
      if (!this.scope) return;
      try {
        this.variants = JSON.parse(this.querySelector('[data-set-main-variants]').textContent);
      } catch {
        return;
      }
      this.busy = false;
      this.scope.addEventListener('change', event => this.onChange(event), {signal: this.lifecycle.signal});
      this.scope.addEventListener('input', event => {
        if (event.target === this.quantityInput) {
          this.clearStatus();
          this.update();
        }
      }, {signal: this.lifecycle.signal});
      this.observer = new MutationObserver(() => this.bindForm());
      this.observer.observe(this.scope, {childList: true, subtree: true});
      this.bindForm();
    }
    disconnectedCallback() {
      this.lifecycle?.abort();
      this.formLifecycle?.abort();
      this.observer?.disconnect();
    }
    bindForm() {
      const form = document.getElementById(this.dataset.formId);
      if (!form || !this.scope.contains(form)) return;
      const quantity = [...form.elements].find(input => input.name === 'quantity');
      const button = form.querySelector('buy-buttons button[type="submit"]');
      if (form === this.form && quantity === this.quantityInput && button === this.button) return;
      if (form !== this.form) {
        this.formLifecycle?.abort();
        this.formLifecycle = new AbortController();
        const signal = this.formLifecycle.signal;
        form.addEventListener('submit', event => this.onSubmit(event), {capture: true, signal});
        form.addEventListener('variant:change', () => {
          this.clearStatus();
          this.update();
        }, {signal});
        form.addEventListener('product:rerender', () => queueMicrotask(() => {
          this.bindForm();
          this.update();
        }), {signal});
      }
      this.form = form;
      this.quantityInput = quantity;
      this.button = button;
      this.rows().forEach(row => this.updateRow(row));
      this.update();
    }
    get variant() {
      const id = this.form?.elements.namedItem('id')?.value;
      return this.variants.find(variant => String(variant.id) === String(id));
    }
    get ready() {
      return Boolean(this.form && this.quantityInput && this.button && this.variants && this.variant);
    }
    validQuantity(quantity, variant = this.variant) {
      if (!variant?.available || !Number.isSafeInteger(quantity)) return false;
      const rule = variant.quantity_rule || {};
      const min = Number(rule.min) || 1;
      const increment = Number(rule.increment) || 1;
      let max = rule.max == null ? Infinity : Number(rule.max);
      if (variant.inventory_management && variant.inventory_policy === 'deny') max = Math.min(max, Number(variant.inventory_quantity));
      return quantity >= min && quantity <= max && (quantity - min) % increment === 0;
    }
    money(cents) {
      const sample = this.dataset.moneySample;
      const number = sample?.match(/\d[\d.,   '’]*\d/)?.[0];
      if (!number) return new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency: this.dataset.currency}).format(cents / 100);
      const fraction = number.match(/([.,])89$/);
      const decimals = fraction ? 2 : 0;
      const integer = fraction ? number.slice(0, -3) : number;
      const group = integer.match(/[^\d]/)?.[0];
      const formatted = new Intl.NumberFormat('en-US', {minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: Boolean(group)}).formatToParts(cents / 100).map(part => {
        if (part.type === 'group') return group;
        if (part.type === 'decimal') return fraction[1];
        return part.value;
      }).join('');
      return sample.replace(number, formatted);
    }
    discount(units) {
      if (this.dataset.discountsEnabled !== 'true' || this.form?.elements.namedItem('selling_plan')?.value) return 0;
      return units >= 3 ? 20 : units >= 2 ? 15 : 0;
    }
    rows() {
      return [...this.querySelectorAll('[data-set-item]')];
    }
    rowItem(row) {
      const option = row.querySelector('[data-set-variant]')?.selectedOptions[0];
      const id = Number(option?.value);
      const price = Number(option?.dataset.price);
      if (!option || option.disabled || option.dataset.available !== 'true' || !Number.isSafeInteger(id) || id <= 0 || !Number.isFinite(price)) return null;
      return {id, quantity: 1, price};
    }
    updateRow(row) {
      const select = row.querySelector('[data-set-variant]');
      const checkbox = row.querySelector('[data-set-check]');
      const option = select?.selectedOptions[0];
      const item = this.rowItem(row);
      checkbox.disabled = !this.ready || !item;
      if (!item) checkbox.checked = false;
      if (option) {
        row.querySelector('[data-set-price]').textContent = option.dataset.money || this.money(Number(option.dataset.price));
        const image = row.querySelector('img');
        if (image && option.dataset.image && image.getAttribute('src') !== option.dataset.image) {
          image.src = option.dataset.image;
          image.removeAttribute('srcset');
        }
      }
    }
    onChange(event) {
      const row = event.target.closest('[data-set-item]');
      const inRow = Boolean(row && this.contains(row));
      if (inRow) this.updateRow(row);
      if (event.target.form === this.form || inRow) {
        this.clearStatus();
        this.update();
      }
    }
    clearStatus() {
      if (this.busy) return;
      const status = this.querySelector('[data-set-status]');
      if (!status) return;
      status.textContent = '';
      status.classList.remove('is-error');
    }
    units() {
      const quantity = Number(this.quantityInput?.value);
      const mainValid = this.ready && this.validQuantity(quantity);
      let count = mainValid ? quantity : 0;
      let total = mainValid ? this.variant.price * quantity : 0;
      this.rows().forEach(row => {
        if (!row.querySelector('[data-set-check]').checked) return;
        const item = this.rowItem(row);
        if (item) {
          total += item.price;
          count += item.quantity;
        }
      });
      return {count, total};
    }
    renderOffer(count, estimate) {
      const level = Math.min(count, 3);
      const keys = ['data-state-zero', 'data-state-one', 'data-state-two', 'data-state-three'];
      const text = this.getAttribute(keys[level]) || '';
      const progress = this.querySelector('[data-set-progress]');
      if (progress) {
        progress.style.setProperty('--inxiz-set-progress', String(level / 3));
        progress.setAttribute('aria-valuenow', String(level));
        progress.setAttribute('aria-valuetext', text);
        progress.querySelectorAll('[data-mark]').forEach(mark => mark.classList.toggle('is-reached', count >= Number(mark.dataset.mark)));
      }
      this.querySelectorAll('[data-set-ladder]').forEach(step => step.classList.toggle('is-reached', count >= Number(step.dataset.setLadder)));
      const state = this.querySelector('[data-set-state]');
      if (state && text) state.textContent = text;
      const shipping = this.querySelector('[data-set-shipping]');
      if (!shipping) return;
      const threshold = Number(this.dataset.freeShippingThreshold);
      if (this.dataset.shippingEnabled !== 'true' || !Number.isFinite(threshold) || threshold <= 0) {
        shipping.hidden = true;
        return;
      }
      shipping.hidden = false;
      shipping.textContent = estimate >= threshold
        ? this.dataset.shippingUnlocked
        : (this.dataset.shippingRemaining || '').replace('[amount]', this.money(threshold - estimate));
    }
    update() {
      if (!this.ready) {
        this.rows().forEach(row => {
          const checkbox = row.querySelector('[data-set-check]');
          checkbox.disabled = true;
          checkbox.checked = false;
        });
        const price = this.button?.querySelector('[data-set-total]');
        if (price) price.hidden = true;
        return;
      }
      const quantity = Number(this.quantityInput.value);
      const valid = this.validQuantity(quantity);
      const {count, total} = this.units();
      const estimate = Math.round(total * (100 - this.discount(count)) / 100);
      this.renderOffer(count, estimate);
      let price = this.button.querySelector('[data-set-total]');
      if (!price) {
        price = document.createElement('span');
        price.className = 'inxiz-set__button-price';
        price.dataset.setTotal = '';
        this.button.append(price);
      }
      price.textContent = valid ? this.money(estimate) : '';
      price.setAttribute('aria-label', `${this.dataset.estimateLabel}: ${price.textContent}`);
      price.hidden = !valid;
      this.button.setAttribute('aria-describedby', `${this.dataset.formId}-estimate-note`);
      if (this.busy) this.button.setAttribute('aria-busy', 'true');
      else this.button.removeAttribute('aria-busy');
    }
    payload() {
      const data = new FormData(this.form);
      const id = Number(data.get('id'));
      const quantity = Number(data.get('quantity'));
      if (!this.validQuantity(quantity) || id !== this.variant?.id) throw new Error(this.dataset.unavailableLabel);
      const main = {id, quantity};
      const properties = {};
      for (const [key, value] of data) {
        const match = key.match(/^properties\[(.*)\]$/);
        if (match && typeof value === 'string') properties[match[1]] = value;
        if (match && typeof value !== 'string') throw new Error(this.dataset.unavailableLabel);
      }
      if (Object.keys(properties).length) main.properties = properties;
      if (data.get('selling_plan')) main.selling_plan = data.get('selling_plan');
      const items = [main];
      this.rows().forEach(row => {
        if (!row.querySelector('[data-set-check]').checked) return;
        const item = this.rowItem(row);
        if (!item) throw new Error(this.dataset.unavailableLabel);
        items.push({id: item.id, quantity: item.quantity});
      });
      return items;
    }
    onSubmit(event) {
      if (!this.ready || (event.submitter && event.submitter !== this.button)) return;
      if (!this.form.checkValidity()) return;
      if ([...this.form.elements].some(input => input.type === 'file' && input.name.startsWith('properties[') && input.files?.length)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return;
      let items;
      try {
        items = this.payload();
      } catch (error) {
        this.status(error.message, true);
        return;
      }
      this.addItems(items);
    }
    status(message, isError = false) {
      const status = this.querySelector('[data-set-status]');
      if (!status) return;
      status.textContent = message;
      status.classList.toggle('is-error', Boolean(message) && isError);
    }
    resetSelection() {
      this.rows().forEach(row => {
        const checkbox = row.querySelector('[data-set-check]');
        if (checkbox) checkbox.checked = false;
      });
      if (this.quantityInput && String(this.quantityInput.value) !== '1' && this.validQuantity(1)) {
        this.quantityInput.value = '1';
        this.quantityInput.dispatchEvent(new Event('change', {bubbles: true}));
      }
    }
    async readCart(sections) {
      const response = await fetch(`${Shopify.routes.root}cart.js`, {headers: {'Accept': 'application/json'}});
      if (!response.ok) throw new Error(this.dataset.uncertainLabel);
      const cart = await response.json();
      const compatibleCart = {
        ...cart,
        cart_level_discount_applications: (cart.cart_level_discount_applications || []).map(application => ({
          ...application,
          discount_application: application.discount_application || application
        }))
      };
      if (sections) compatibleCart.sections = sections;
      return compatibleCart;
    }
    async readSections(sections, variantId) {
      const response = await fetch(`${Shopify.routes.root}variants/${variantId}?sections=${encodeURIComponent(sections.join(','))}`, {headers: {'Accept': 'application/json'}});
      if (!response.ok) throw new Error(this.dataset.cartUnavailableLabel);
      const rendered = await response.json();
      if (sections.some(section => typeof rendered[section] !== 'string')) throw new Error(this.dataset.cartUnavailableLabel);
      return rendered;
    }
    async addItems(items) {
      if (this.busy) return;
      this.busy = true;
      this.status('');
      this.update();
      const form = this.form;
      let confirmed = false;
      let succeeded = false;
      let message = '';
      let isError = false;
      let result;
      let rejected = false;
      const sections = ['variant-added'];
      try {
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {bubbles: true, detail: {sections}}));
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: JSON.stringify({items, sections: sections.join(','), sections_url: `${Shopify.routes.root}variants/${items[0].id}`})
        });
        result = await response.json();
        if (!response.ok) {
          rejected = true;
          throw new Error(typeof result.description === 'string' ? result.description : this.dataset.errorLabel);
        }
        confirmed = true;
        const cart = await this.readCart(result.sections);
        if (sections.some(section => typeof cart.sections?.[section] !== 'string')) cart.sections = await this.readSections(sections, items[0].id);
        form.dispatchEvent(new CustomEvent('variant:add', {bubbles: true, detail: {items: result.items || [result], cart}}));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {bubbles: true, detail: {baseEvent: 'variant:add', cart}}));
        const units = items.reduce((sum, item) => sum + item.quantity, 0);
        message = units === 1 ? this.dataset.addedOneLabel : (this.dataset.addedManyLabel || '').replace('[pieces]', String(units));
        succeeded = true;
        if (window.themeVariables?.settings.cartType === 'page' || window.themeVariables?.settings.pageType === 'cart') location.assign(`${Shopify.routes.root}cart`);
      } catch (error) {
        try {
          const cart = await this.readCart();
          cart.sections = await this.readSections(sections, items[0].id);
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {bubbles: true, detail: {baseEvent: 'cart:refresh', cart}}));
        } catch {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
        message = confirmed ? this.dataset.cartUnavailableLabel : (rejected ? error.message : this.dataset.uncertainLabel);
        isError = true;
      } finally {
        if (succeeded || confirmed) this.resetSelection();
        this.busy = false;
        this.update();
        this.status(message, isError);
      }
    }
  });
}
